import { useEffect } from "react";
import { SITE, IMAGES } from "@/lib/config";
import { useLang } from "@/lib/i18n";

const upsertMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, href, extra = {}) => {
  const selector = `link[rel="${rel}"]${extra.hreflang ? `[hreflang="${extra.hreflang}"]` : ""}`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export const Seo = ({ title, description, path = "/", image = IMAGES.og, type = "website", jsonLd }) => {
  const { lang } = useLang();
  useEffect(() => {
    const url = `${SITE.domain}${path}`;
    const img = image.startsWith("http") ? image : `${SITE.domain}${image}`;
    document.title = title;
    upsertMeta("name", "description", description);
    upsertLink("canonical", url);
    upsertLink("alternate", url, { hreflang: "en" });
    upsertLink("alternate", url, { hreflang: "es" });
    upsertLink("alternate", url, { hreflang: "x-default" });
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", img);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", SITE.name);
    upsertMeta("property", "og:locale", lang === "es" ? "es_US" : "en_US");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);
  }, [title, description, path, image, type, lang]);

  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : null;
  useEffect(() => {
    if (!jsonLdKey) return undefined;
    const parsed = JSON.parse(jsonLdKey);
    const items = Array.isArray(parsed) ? parsed : [parsed];
    const scripts = items.map((data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo", "page");
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    });
    return () => scripts.forEach((s) => s.remove());
  }, [jsonLdKey]);

  return null;
};
